import React from 'react';
import { PageHero, CTASection, SectionHeader } from '@/components/shared/Section';
import { VALUES } from '@/data/content';
import { Quote, Target, Eye } from 'lucide-react';

const aboutImg = 'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?w=1600&q=80';

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About TITO"
        title="A Dubai-based partner for businesses that want to grow with confidence."
        description="TITO Business Growth & Workforce Solutions is a business advisory and workforce solutions company helping businesses grow through strategy, recruitment, employer branding and digital workforce solutions."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 bg-tito-gold/20 rounded-sm blur-2xl" />
              <img src={aboutImg} alt="TITO team" className="relative rounded-sm w-full h-[480px] object-cover border border-slate-200" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="eyebrow text-tito-blue/70">Who we are</div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
              Practical solutions built on real-world experience.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              With extensive experience spanning banking, business development, real estate, relationship management and entrepreneurship, we provide practical solutions that help businesses improve productivity, attract talent and achieve sustainable growth.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-sm border border-slate-200 bg-white p-6">
                <Target className="h-5 w-5 text-tito-gold" />
                <h3 className="mt-4 font-display font-semibold text-lg text-slate-900">Our Mission</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  To empower businesses with strategic solutions that enhance growth, improve workforce management and create long-term success.
                </p>
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-6">
                <Eye className="h-5 w-5 text-tito-gold" />
                <h3 className="mt-4 font-display font-semibold text-lg text-slate-900">Our Vision</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  To become a leading business growth and workforce solutions provider — connecting businesses with opportunities through innovation and technology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <SectionHeader
            eyebrow="Our core values"
            title="The principles that guide how we work."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v, i) => (
              <div key={v.name} className="rounded-sm border border-slate-200 bg-[#F8FAFC] p-7 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="font-display font-bold text-tito-gold">0{i + 1}</div>
                <h4 className="mt-3 font-display font-semibold text-xl text-slate-900">{v.name}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="rounded-sm border border-slate-200 bg-white p-10 md:p-16 relative overflow-hidden">
          <Quote className="absolute top-6 right-6 h-16 w-16 text-tito-gold/15" />
          <div className="eyebrow text-tito-blue/70">Future innovation</div>
          <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-slate-900 max-w-3xl">
            We are committed to developing technology-enabled workforce solutions that improve how businesses connect with skilled professionals and manage recruitment more efficiently.
          </h3>
        </div>
      </section>

      <CTASection />
    </>
  );
}
